import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { State } from "../../state/reducers";
import {
  createConfig,
  createSystem,
  deleteConfig,
  deleteSystem,
  duplicateConfig,
  saveOrUpdateSystem
} from '../../state/system/action_creator';

import { validateCanSaveSystem, findSystemByCode, getSystemAndConfigs, isDuplicateSystemCode } from "../../components/system/systemHelpers";
import { SystemSection } from "../../context/SystemsContext";
// ... import all your actions and helpers ...

export const useSystemsManager = () => {
  const dispatch = useDispatch();
  const { selectedSystem, systems } = useSelector((state: State) => state.systemState);

  // All state management is now encapsulated here
  const [configs, setConfigs] = useState<any[]>([]);
  const [expandedConfigId, setExpandedConfigId] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<SystemSection>("systemInfo");
  const [formData, setFormData] = useState<any>({});
  const [dirty, setDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Actions

  const createSystemAction = bindActionCreators(createSystem, dispatch);
  const deleteSystemAction = bindActionCreators(deleteSystem, dispatch);
  const updateSystemAction = bindActionCreators(saveOrUpdateSystem, dispatch);
  const deleteConfigAction = bindActionCreators(deleteConfig, dispatch);

  const systemObj = useMemo(() => findSystemByCode(systems, selectedSystem), [systems, selectedSystem]);

  // All useEffects are now part of the hook's lifecycle
  useEffect(() => {
    const { system, configs } = getSystemAndConfigs(systems, selectedSystem);
    setConfigs(configs);
    if (system) {
      setFormData(system);
      setDirty(false);
    }
  }, [selectedSystem, systems, systemObj]);

  useEffect(() => {
    if (selectedSection === "systemInfo" || selectedSection === "createSystem") return;

    if (!expandedConfigId && configs.length > 0) {
      const defaultCfg = configs.find((c: any) => c.clientProcess?.is_default);
      if (defaultCfg) {
        setExpandedConfigId(defaultCfg.clientProcess.name);
        setSelectedSection("clientProcess");
      }
    }
  }, [configs, expandedConfigId, selectedSection]);

  // All handlers are now internal to the hook
  const handleChange = (key: string, value: any) => {
    console.log(key, value);
    setFormData((prev: any) => {
      const updated = structuredClone(prev);
      const keys = key.split(".");
      let obj = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in obj) || typeof obj[k] !== "object" || obj[k] === null) {
          obj[k] = {}; // ensure nested object exists
        }
        obj = obj[k];
      }
      obj[keys[keys.length - 1]] = value;

      const isDifferent = JSON.stringify(updated) !== JSON.stringify(baseSystem);
      setDirty(isDifferent);
      return updated;
    });
  };

  const saveChanges = async () => {
    const newCode = formData.systemCode.trim();

    if (selectedSection === "createSystem") {
      if (isDuplicateSystemCode(newCode, systems)) {
        alert(`A system with the code "${newCode}" already exists. Please choose a unique code.`);
        return;
      }

      setIsSaving(true);
      try {
        await createSystemAction(formData);
        setSelectedSection("systemInfo");
        setDirty(false);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const result = validateCanSaveSystem(formData, expandedConfigId || "");
    if (!result) return;

    const { updated, newConfigName } = result;

    setIsSaving(true);
    try {
      await updateSystemAction(updated);
      setDirty(false);
      if (newConfigName && newConfigName !== expandedConfigId) {
        setExpandedConfigId(newConfigName);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const createNewSystem = () => {
    // 🔹 Reset to blank state
    setSelectedSection("createSystem");
    setFormData({
      systemCode: "",
      unitIndex: "U01",
      commonName: "",
      cloudFrontBaseUrl: "",
      customerDealIdentifier: "",
      dailyAssetReportEmailList: "",
      IANA_timezone: "",
      configurations: {},
    });
    setConfigs([]); // ✅ clear configurations
    setExpandedConfigId(null); // collapse any open config
    setDirty(false);
  };

  const cancelCreateSystem = () => {
    const { system, configs } = getSystemAndConfigs(systems, selectedSystem);
    setFormData(system || {});
    setConfigs(configs);
    setSelectedSection("systemInfo");
    setExpandedConfigId(null);
    setDirty(false);
  };

  const deleteCurrentSystem = () => {
    if (
      window.confirm(
        `Are you sure you want to delete system "${systemObj.systemCode}"? This action cannot be undone.`
      )
    ) {
      deleteSystemAction(systemObj.systemCode);
    }
  }


  const createNewConfig = async () => {
    if (!formData) return;

    const newConfig = await dispatch(createConfig(formData) as any);
    if (!newConfig) return;

    setExpandedConfigId(newConfig.clientProcess.name);
    setSelectedSection("clientProcess");
  };

  const deleteSelectedConfig = () => {
    if (!expandedConfigId || !formData) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete configuration "${expandedConfigId}"?`
    );
    if (!confirmed) return;

    deleteConfigAction(formData, expandedConfigId);
    setExpandedConfigId(null);
    setSelectedSection("systemInfo");
  };

  const duplicateSelectedConfig = async () => {
    if (!expandedConfigId || !formData) return;
    const duplicated = await dispatch(duplicateConfig(formData, expandedConfigId) as any);
    if (!duplicated) return;

    setExpandedConfigId(duplicated.clientProcess.name);
    setSelectedSection("clientProcess");
  };

  const selectConfig = (configId: string | null) => {
    if (configId === expandedConfigId) {
      setExpandedConfigId(null);
      setSelectedSection("systemInfo");
    } else {
      setExpandedConfigId(configId);
      if (selectedSection === "systemInfo") {
        setSelectedSection("clientProcess");
      }
    }
  };

  const baseSystem = useMemo(() => (selectedSection === "createSystem" ? formData : systemObj || {}), [
    systemObj,
    selectedSection,
  ]);
  const canSave = (selectedSection === "createSystem" && formData.systemCode?.trim() && formData.commonName?.trim()) || (selectedSection !== "createSystem" && dirty);
  const isLoadingSystem = !Array.isArray(systems) || (selectedSystem && !systemObj);

  // The hook returns an object that matches our context's interface
  return {
    formData,
    configs,
    selectedSection,
    expandedConfigId,
    dirty,
    isSaving,
    isLoadingSystem,
    canSave,
    handleChange,
    saveChanges,
    selectSection: setSelectedSection,
    selectConfig,
    selectSystemInfo: () => {
      setExpandedConfigId(null);
      setSelectedSection("systemInfo");
    },
    createNewSystem,
    cancelCreateSystem,
    deleteCurrentSystem,
    createNewConfig,
    deleteSelectedConfig,
    duplicateSelectedConfig
    // ... return all other handlers with context-friendly names ...
  };
};